import { Logo } from "@components/atoms/Logo"
import { SearchOverlay } from "@components/atoms/search/SearchOverlay"
import { LogoRotation } from "@components/molecules/LogoRotation"
import { Navigation } from "@components/molecules/Navigation"
import { useMainSectionsNav, usePopularSearches } from "@hooks/queries"
import { useHeaderVisibility } from "@hooks/useHeaderVisibility"
import { useNav } from "@hooks/useNav"
import { useSearch } from "@hooks/useSearch"
import { useBreakpointIsBelow, useIsMobile } from "@hooks/useViewportDerived"
import { Link, useHydrating, useRoute, useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import * as Icons from "@components/atoms/Icons"
import { useAppData } from "eddev/hooks"
import { Button } from "@components/atoms/Button"
import { HomesNav } from "@components/atoms/HomePagesNavigation"

export function Header() {
  const nav = useNav()
  const route = useRoute()
  const mobile = useIsMobile()
  const tablet = useBreakpointIsBelow("laptop")
  const hydrating = useHydrating()
  const mainSections = useMainSectionsNav(undefined)
  const search = useSearch()
  const enableAISearch = useAppData((data) => data?.aiSearchSettings?.settings?.enable)

  useEffect(() => {
    nav.setOpen(false)
  }, [route.pathname])

  const headerRef = useRef<HTMLDivElement>(null)

  const { hidden, setHidden, setHeight } = useHeaderVisibility()
  const lastScrollY = useRef(0)
  const ticking = useRef(false)
  const [pastIntro, setPastIntro] = useState(false)

  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      if (headerRef.current) {
        setHeight(headerRef.current.offsetHeight)
      }
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    lastScrollY.current = window.scrollY

    const updateScrollState = () => {
      ticking.current = false
      const currentScrollY = window.scrollY
      const delta = currentScrollY - lastScrollY.current

      if (nav.open) {
        setHidden(false)
      } else if (currentScrollY < 80) {
        setHidden(false)
      } else if (delta > 10) {
        setHidden(true)
      } else if (delta < -10) {
        setHidden(false)
      }

      setPastIntro(currentScrollY >= 300)

      lastScrollY.current = currentScrollY
    }

    const handleScroll = () => {
      if (!ticking.current) {
        ticking.current = true
        requestAnimationFrame(updateScrollState)
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [nav.open])

  return (
    <>
      <div className="fixed top-0 left-0 z-[3] w-full" ref={headerRef}>
        <motion.div
          className={twMerge(
            "py-5 gap-y-4 flex items-center w-full flex-wrap",
            "before:content-[''] before:absolute before:inset-0 before:z-[-1] before:bg-light-grey before:transition-all before:ease-menu before:duration-700",
            "after:content-[''] after:absolute after:inset-0 after:z-[-2] after:bg-header-background",
            "[--search-height:42px] md:[--search-height:48px]",
            nav.open ? "before:opacity-100" : "before:opacity-0",
          )}
          animate={{ y: hidden ? "-100%" : "0%" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <div className="grid-auto !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] gap-0 items-center px-4 md:px-6 xxl:px-0">
            <div className={twMerge(enableAISearch ? "col-span-4" : "col-span-3")}>
              {!hydrating && mainSections.data?.siteSettings?.themeSiteSettings?.mainSections ? (
                <Link
                  className={twMerge(
                    "w-full max-w-[110px] h-12 flex transition-all ease-menu duration-700 [&_svg]:w-full [&_svg]:h-auto",
                    nav.open ? "text-black" : "text-logo-text",
                  )}
                  href={mainSections.data.siteSettings.themeSiteSettings.mainSections[0]?.uri}
                >
                  <Logo />
                </Link>
              ) : (
                <div
                  className={twMerge(
                    "w-full max-w-[110px] h-12 flex transition-all ease-menu duration-700 [&_svg]:w-full [&_svg]:h-auto",
                    nav.open ? "text-black" : "text-logo-text",
                  )}
                >
                  <Logo />
                </div>
              )}
            </div>

            <div className={twMerge(enableAISearch ? "col-span-8" : "col-span-9")}>
              <div className="flex items-center justify-end gap-3">
                {!mobile && nav.open && (
                  <motion.div className="opacity-0 flex items-center gap-3" animate={{ opacity: 1 }}>
                    <Link
                      href="/contact-us/"
                      className={twMerge(
                        "!rounded-[6px] cursor-pointer !flex items-center justify-center text-center",
                        "transition-[background-color,color,box-shadow] ease-movement duration-300",
                        "no-underline !border-none",
                        "disabled:cursor-not-allowed",
                        "[&:has(svg)_span]:grow [&:has(svg)_span]:text-left",
                        "!bg-light-grey !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-mid-grey min-h-6 min-w-6 !p-3 type-label-s box-border !leading-[1.2] md:max-h-7",
                      )}
                    >
                      Contact us
                    </Link>
                    <Link
                      href="/about-the-aaa/our-members/"
                      className={twMerge(
                        "!rounded-[6px] cursor-pointer !flex items-center justify-center text-center",
                        "transition-[background-color,color,box-shadow] ease-movement duration-300",
                        "no-underline !border-none",
                        "disabled:cursor-not-allowed",
                        "[&:has(svg)_span]:grow [&:has(svg)_span]:text-left",
                        "!bg-light-grey !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-mid-grey min-h-6 min-w-6 !p-3 type-label-s box-border !leading-[1.2] md:max-h-7",
                        "grow overflow-hidden whitespace-nowrap inline-block no-underline transition-[max-width,margin] ease-movement duration-300",
                        "ml-3 text-right",
                      )}
                    >
                      {Icons["ASSISTANCE"]}
                      <span>Roadside Assistance</span>
                    </Link>
                  </motion.div>
                )}

                <div
                  className={twMerge(
                    "flex items-center rounded-md px-2 h-[var(--search-height)] gap-2 transition-all ease-menu duration-700",
                    nav.open ? "bg-white" : "bg-searchBg",
                  )}
                >
                  <LogoRotation color={false} />
                  <Link
                    href={
                      route.pathname.includes("international-motoring/international-driving-permits")
                        ? "https://www.internationaldrivingpermitsonline.com.au/"
                        : "/international-motoring/international-driving-permits/"
                    }
                    target="_blank"
                    className={twMerge(
                      "!rounded-[6px] cursor-pointer !flex items-center justify-center text-center",
                      "transition-[background-color,color,box-shadow] ease-movement duration-300",
                      "no-underline !border-none",
                      "disabled:cursor-not-allowed",
                      "[&:has(svg)_span]:grow [&:has(svg)_span]:text-left",
                      "!bg-yellow !text-black hover:!bg-hover-yellow hover:!text-black active:!bg-active-yellow active:!text-black disabled:!bg-disabled-yellow disabled:!text-grey",
                      "min-h-6 min-w-6 !p-3 type-label-s box-border !leading-[1.2] md:max-h-7",
                    )}
                  >
                    {mobile ? `Get your IDP` : `Apply for an IDP`}
                  </Link>
                </div>

                {!enableAISearch && (
                  <button
                    className={twMerge(
                      "!rounded-md cursor-pointer !flex items-center justify-center text-center",
                      "transition-[background-color,color,box-shadow] ease-movement duration-300",
                      "no-underline !border-none",
                      "disabled:cursor-not-allowed",
                      "[&:has(svg)_span]:grow [&:has(svg)_span]:text-left",
                      "h-[var(--search-height)] w-[var(--search-height)]",
                      nav.open
                        ? "!bg-white !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-white"
                        : "!bg-searchBg !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-mid-grey",
                    )}
                    onClick={() => {
                      nav.setOpen(false)
                      search.setOpen(!search.open)
                    }}
                  >
                    {search.open ? Icons["CLOSE"] : Icons["SEARCH"]}
                  </button>
                )}

                <button
                  className={twMerge(
                    "!rounded-md cursor-pointer !flex items-center justify-center text-center",
                    "transition-[background-color,color,box-shadow] ease-movement duration-300",
                    "no-underline !border-none",
                    "disabled:cursor-not-allowed",
                    "[&:has(svg)_span]:grow [&:has(svg)_span]:text-left",
                    "h-[var(--search-height)] w-[var(--search-height)]",
                    nav.open
                      ? "!bg-white !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-white"
                      : "!bg-searchBg !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-mid-grey",
                  )}
                  onClick={() => {
                    nav.setOpen(!nav.open)
                    search.setOpen(false)
                  }}
                >
                  {nav.open ? Icons["CLOSE"] : Icons["HAMBURGER"]}
                </button>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {!tablet && !nav.open && !enableAISearch && (route.view !== "templates/home-pages" || pastIntro) && (
              <motion.div
                className="absolute top-[-2px] left-1/2 -translate-x-1/2 z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <HomesNav />
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>{search.open && !enableAISearch && <SearchDropdown />}</AnimatePresence>
          {mobile && enableAISearch && <SearchOverlay />}
        </motion.div>
      </div>
      <Navigation />
    </>
  )
}

const SearchDropdown = () => {
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState<string>("")
  const search = useSearch()
  const popularSearches = usePopularSearches(undefined).data?.siteSettings?.themeSiteSettings?.searchTerms
  // left-[calc(50%+(var(--tw-grid-inner-width)*(1/2)))]
  return (
    <motion.div
      className={twMerge(
        "p-5 bg-light-grey rounded-md absolute top-full -translate-x-full shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]",
        "left-[calc(100%-var(--spacing-4))]",
        "md:left-[calc(100%-var(--spacing-6))]",
        "xl:left-[calc((50vw+(1600px*(1/2)))-var(--spacing-6))]",
        "xxl:left-[calc(50vw+(1720px*(1/2)))]",
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          router?.navigate(`/search?q=${encodeURIComponent(value)}`)
          search.setOpen(false)
        }}
      >
        <input
          className="type-label-l px-3 rounded-sm border-none shadow-[inset_0px_0px_0px_1px_#000000] focus:outline-none focus:shadow-[inset_0px_0px_0px_2px_#000000]"
          type="text"
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
        />
        <Button icon="SEARCH" size="small" color="outline" />
      </form>

      <div className="mt-5 flex flex-col gap-4">
        <h5 className="type-title-s my-0">Popular Searches</h5>

        <ul className="flex flex-col gap-3">
          {popularSearches?.map((term, i) => (
            <li key={i} className="type-label-s flex items-center">
              {Icons.CARET_RIGHT}{" "}
              <Link
                className="hover:underline"
                href={`/search?q=${encodeURIComponent(term?.searchTerm?.toLowerCase() ?? "")}`}
                onClick={() => search.setOpen(false)}
              >
                {term?.searchTerm}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  )
}
